import React, { useEffect, useState } from "react";
import { Check, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

function gridSizeFor(width) {
  return width < 480 ? { cols: 7, hexW: 28, hexH: 25, gap: 4 } : { cols: 10, hexW: 36, hexH: 32, gap: 5 };
}

function useHexGridSize() {
  const [size, setSize] = useState(() => gridSizeFor(typeof window !== "undefined" ? window.innerWidth : 1024));
  useEffect(() => {
    const onResize = () => setSize(gridSizeFor(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
}

export function DriverColorPicker({ open, onOpenChange, driverName, colors, currentColor, usedColors, onSelect }) {
  const { cols, hexW, hexH, gap } = useHexGridSize();
  const rowStep = hexH * 0.75 + gap;

  function hexPosition(index) {
    const row = Math.floor(index / cols);
    const col = index % cols;
    const xOffset = row % 2 === 1 ? (hexW + gap) / 2 : 0;
    return { left: col * (hexW + gap) + xOffset, top: row * rowStep };
  }

  const rows = Math.ceil(colors.length / cols);
  const width = cols * (hexW + gap) + (hexW + gap) / 2;
  const height = rows > 0 ? (rows - 1) * rowStep + hexH : 0;
  const normalizedCurrent = (currentColor || "").toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="driver-color-picker-dialog">
        <DialogHeader>
          <DialogTitle>Choose {driverName ? `${driverName}'s` : "this driver's"} color</DialogTitle>
          <DialogDescription>
            100 distinct colors to pick from. Greens are excluded on purpose — reserved for "delivered" everywhere on the map.
          </DialogDescription>
        </DialogHeader>
        <div className="relative mx-auto py-2" style={{ width, height }} data-testid="driver-color-picker-grid">
          {colors.map((hex, i) => {
            const upper = hex.toUpperCase();
            const isCurrent = upper === normalizedCurrent;
            const isUsed = usedColors.has(upper) && !isCurrent;
            const pos = hexPosition(i);
            return (
              <button
                key={hex}
                type="button"
                title={isUsed ? `${hex} — already used by another driver` : hex}
                disabled={isUsed}
                onClick={() => onSelect(hex)}
                className="absolute flex items-center justify-center transition-transform duration-150 hover:z-10 hover:scale-125 disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:scale-100"
                style={{
                  left: pos.left - (isCurrent ? 3 : 0),
                  top: pos.top - (isCurrent ? 3 : 0),
                  width: hexW + (isCurrent ? 6 : 0),
                  height: hexH + (isCurrent ? 6 : 0),
                  padding: isCurrent ? 3 : 0,
                  borderRadius: isCurrent ? 6 : 0,
                  outline: isCurrent ? "2px solid #111827" : "none",
                  outlineOffset: isCurrent ? 1 : 0,
                }}
                data-testid={`driver-color-swatch-${hex.replace("#", "")}`}
              >
                <span
                  className="flex h-full w-full items-center justify-center"
                  style={{ background: hex, clipPath: "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)" }}
                >
                  {isCurrent && <Check size={12} className="text-white drop-shadow" strokeWidth={3} />}
                  {isUsed && <Lock size={9} className="text-white/85" strokeWidth={2.5} />}
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
